import { Facebook, Instagram, Linkedin, Mail, MapPin } from "lucide-react";

import { getProduct } from "@base/catalog/products";
import { getCommonCopy } from "@base/content";
import { ORG } from "@base/site/config";

const WhatsappIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    width="18"
    height="18"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const linkClass = "text-sm text-white/80 hover:text-white transition-colors";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const copy = getCommonCopy().footer;
  const bootgly = getProduct("bootgly").links;

  const columns = [
    {
      title: copy.sections.quickLinks,
      links: [
        { href: "/apps", label: copy.links.apps },
        { href: "/business", label: copy.links.business },
        { href: "/business#servicos", label: copy.links.services },
        { href: "/blog/", label: copy.links.blog },
      ],
    },
    {
      title: copy.sections.developers,
      links: [
        { href: "/developers", label: copy.links.developers },
        { href: bootgly.site, label: copy.links.bootgly, external: true },
        { href: bootgly.docs, label: copy.links.bootglyDocs, external: true },
        { href: "/developers#dataruni", label: copy.links.dataruni },
      ],
    },
    {
      title: copy.sections.institutional,
      links: [
        { href: "/terms", label: copy.links.terms },
        { href: "/privacy", label: copy.links.privacy },
        { href: "/cookies", label: copy.links.cookies },
      ],
    },
  ];

  return (
    <footer className="bg-[#6c2db4] border-t border-border/20 text-white">
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-8 md:gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <img
              src="/logo.webp"
              alt="Codecious Logo"
              width="200"
              height="48"
              className="h-12 w-auto"
            />
            <p className="text-sm text-white/80 leading-relaxed">
              {copy.tagline}
            </p>
            <div className="flex gap-3">
              <a
                href={ORG.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={copy.social.linkedin}
                className="text-white/80 hover:text-white transition-colors"
              >
                <Linkedin size={20} />
              </a>
              <a
                href={ORG.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={copy.social.instagram}
                className="text-white/80 hover:text-white transition-colors"
              >
                <Instagram size={20} />
              </a>
              <a
                href={ORG.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={copy.social.facebook}
                className="text-white/80 hover:text-white transition-colors"
              >
                <Facebook size={20} />
              </a>
            </div>
          </div>

          {columns.map(column => (
            <div key={column.title} className="space-y-4">
              <h3 className="font-semibold text-white">{column.title}</h3>
              <ul className="space-y-2">
                {column.links.map(link => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className={linkClass}
                      {...("external" in link && link.external
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="font-semibold text-white">
              {copy.sections.contact}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-white mt-0.5 flex-shrink-0" />
                <span className="text-sm text-white/80">
                  {ORG.address.street}
                  <br />
                  {ORG.address.city}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-white flex-shrink-0" />
                <a href={`mailto:${ORG.email}`} className={linkClass}>
                  {ORG.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <WhatsappIcon className="text-white flex-shrink-0" />
                <a
                  href={ORG.whatsapp.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                >
                  {ORG.whatsapp.display}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/20 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-center md:text-left">
            <p className="text-sm text-white/80">
              © {currentYear} {ORG.name}. {copy.rights}
            </p>
            <p className="text-sm text-white/80">CNPJ: {ORG.cnpj}</p>
          </div>
          <p className="text-sm text-white/80">{copy.madeWith}</p>
        </div>
      </div>
    </footer>
  );
}
